/*
 * Marketplace specific configuration.
 *
 * Every filter needs to have following keys:
 * - id:     Unique id of the filter.
 * - label:  The default label of the filter.
 * - type:   String that represents one of the existing filter components:
 *           BookingDateRangeFilter, KeywordFilter, PriceFilter,
 *           SelectSingleFilter, SelectMultipleFilter.
 * - group:  Is this 'primary' or 'secondary' filter?
 *           Primary filters are visible on desktop layout by default.
 *           Secondary filters are behind "More filters" button.
 *           Read more from src/containers/SearchPage/README.md
 * - queryParamNames: Describes parameters to be used with queries
 *                    (e.g. 'price' or 'pub_amenities'). Most of these are
 *                    the same between webapp URLs and API query params.
 *                    You can't change 'dates', 'price', or 'keywords'
 *                    since those filters are fixed to a specific attribute.
 * - config: Extra configuration that the filter component needs.
 *
 * Note 1: Labels could be tied to translation file
 *         by importing FormattedMessage:
 *         <FormattedMessage id="some.translation.key.here" />
 *
 * Note 2: If you need to add new custom filter components,
 *         you need to take those into use in:
 *         src/containers/SearchPage/FilterComponent.js
 *
 * Note 3: If you just want to create more enum filters
 *         (i.e. SelectSingleFilter, SelectMultipleFilter),
 *         you can just add more configurations with those filter types
 *         and tie them with correct extended data key
 *         (i.e. pub_<key> or meta_<key>).
 */

export const filters = [
  {
    id: 'category',
    label: 'Category',
    type: 'SelectSingleFilter',
    group: 'primary',
    queryParamNames: ['pub_category'],
    config: {
      // Schema type is enum for SelectSingleFilter
      schemaType: 'enum',

      // "key" is the option you see in Flex Console.
      // "label" is set here for the UI only.
      // Note: label is not added through the translation files
      // to make filter customizations a bit easier.
      options: [{ key: 'designer', label: 'Designer' }, { key: 'vintage', label: 'Vintage' }],
    },
  },
  {
    id: 'brand',
    label: 'Brand',
    type: 'SelectMultipleFilter',
    group: 'primary',
    queryParamNames: ['pub_brand'],
    config: {
      // Schema type is enum for SelectSingleFilter
      searchMode: 'has_any',
      // "key" is the option you see in Flex Console.
      // "label" is set here for the UI only.
      // Note: label is not added through the translation files
      // to make filter customizations a bit easier.
      options: [
        { key: '&Tradition', label: '&Tradition', category: 'A - F'},
        { key: 'Alias', label: 'Alias', category: ''},
        { key: 'Alessi', label: 'Alessi', category: 'A - F'},
        { key: 'Anglepoise', label: 'Anglepoise', category: 'A - F'},
        { key: 'AnotherCountry', label: 'Another Country', category: 'A - F'},
        { key: 'Arhaus', label: 'Arhaus', category: 'A - F'},
        { key: 'ArmaniCasa', label: 'Armani Casa', category: 'A - F'},
        { key: 'Artek', label: 'Artek', category: 'A - F'},
        { key: 'Artemide', label: 'Artemide', category: 'A - F'},
        { key: 'AstierdeVillatte', label: 'Astier de Villatte', category: 'A - F'},
        { key: 'B&BItalia', label: 'B&B Italia', category: 'A - F'},
        { key: 'Bernardaud', label: 'Bernardaud', category: 'A - F'},
        { key: 'BitossiHome', label: 'Bitossi Home', category: 'A - F'},
        { key: 'BobandFriends', label: 'Bob and Friends', category: 'A - F'},
        { key: 'BoConcept', label: 'BoConcept', category: 'A - F'},
        { key: 'Bonaldo', label: 'Bonaldo', category: 'A - F'},
        { key: 'BonnieandNeil', label: 'Bonnie and Neil', category: 'A - F'},
        { key: 'Bulthaup', label: 'Bulthaup', category: 'A - F'},
        { key: 'Burleigh', label: 'Burleigh', category: 'A - F'},
        { key: 'BvlgariHome', label: 'Bvlgari Home', category: 'A - F'},
        { key: 'Cane-line', label: 'Cane-line', category: 'A - F'},
        { key: 'Capellini', label: 'Capellini', category: 'A - F'},
        { key: 'Cappellini', label: 'Cappellini', category: 'A - F'},
        { key: 'CarlHansen&Son', label: 'Carl Hansen & Søn', category: 'A - F'},
        { key: 'Cassina', label: 'Cassina', category: 'A - F'},
        { key: 'CB2', label: 'CB2', category: 'A - F'},
        { key: 'ChristianLiaigre', label: 'Christian Liaigre', category: 'A - F'},
        { key: 'ChurchillChina', label: 'Churchill China', category: 'A - F'},
        { key: 'Citta', label: 'Città', category: 'A - F'},
        { key: 'CocoRepublic', label: 'Coco Republic', category: 'A - F'},
        { key: 'ColefaxandFowler', label: 'Colefax and Fowler', category: 'A - F'},
        { key: 'Crate&Barrel', label: 'Crate & Barrel', category: 'A - F'},
        { key: 'CultDesign', label: 'Cult Design', category: 'A - F'},
        { key: 'DanskeMobler', label: 'Danske Møbler', category: 'A - F'},
        { key: 'DavidLinley', label: 'David Linley', category: 'A - F'},
        { key: 'DavidTrubridge', label: 'David Trubridge', category: 'A - F'},
        { key: 'DeSede', label: 'De Sede', category: 'A - F'},
        { key: 'Denby', label: 'Denby', category: 'A - F'},
        { key: 'DesignDenmark', label: 'Design Denmark', category: 'A - F'},
        { key: 'DesignWithinReach', label: 'Design Within Reach', category: 'A - F'},
        { key: 'Dolce&GabbanaCasa', label: 'Dolce & Gabbana Casa', category: 'A - F'},
        { key: 'DouglasandBec', label: 'Douglas and Bec', category: 'A - F'},
        { key: 'Driade', label: 'Driade', category: 'A - F'},
        { key: 'ECC', label: 'ECC', category: 'A - F'},
        { key: 'Eichholtz', label: 'Eichholtz', category: 'A - F'},
        { key: 'EmmaBridgewater', label: 'Emma Bridgewater', category: 'A - F'},
        { key: 'Ercol', label: 'Ercol', category: 'A - F'},
        { key: 'FaienceriedeGien', label: 'Faïencerie de Gien', category: 'A - F'},
        { key: 'FendiCasa', label: 'Fendi Casa', category: 'A - F'},
        { key: 'Fenton&Fenton', label: 'Fenton & Fenton', category: 'A - F'},
        { key: 'FermLiving', label: 'Ferm Living', category: 'A - F'},
        { key: 'Fermob', label: 'Fermob', category: 'A - F'},
        { key: 'Flexform', label: 'Flexform', category: 'A - F'},
        { key: 'Flos', label: 'Flos', category: 'A - F'},
        { key: 'Forma', label: 'Forma', category: 'A - F'},
        { key: 'Foscarini', label: 'Foscarini', category: 'A - F'},
        { key: 'Fredericia', label: 'Fredericia', category: 'A - F'},
        { key: 'FritzHansen', label: 'Fritz Hansen', category: 'A - F'},
        { key: 'Fromental', label: 'Fromental', category: 'A - F'},
        { key: 'GPlan', label: 'G Plan', category: 'G - M'},
        { key: 'Georg Jensen', label: 'Georg Jensen', category: 'G - M'},
        { key: 'Ginori1735', label: 'Ginori 1735', category: 'G - M'},
        { key: 'Giorgetti', label: 'Giorgetti', category: 'G - M'},
        { key: 'GlobeWest', label: 'GlobeWest', category: 'G - M'},
        { key: 'GoodForm', label: 'Good Form', category: 'G - M'},
        { key: 'GregNatale', label: 'Greg Natale', category: 'G - M'},
        { key: 'Grythyttan', label: 'Grythyttan', category: 'G - M'},
        { key: 'Gubi', label: 'Gubi', category: 'G - M'},
        { key: 'GucciDecor', label: 'Gucci Décor', category: 'G - M'},
        { key: 'Haviland', label: 'Haviland', category: 'G - M'},
        { key: 'Hay', label: 'Hay', category: 'G - M'},
        { key: 'Heals', label: 'Heal\'s', category: 'G - M'},
        { key: 'HEM', label: 'HEM', category: 'G - M'},
        { key: 'HermanMiller', label: 'Herman Miller', category: 'G - M'},
        { key: 'HermesMaison', label: 'Hermès Maison', category: 'G - M'},
        { key: 'HouseofHackney', label: 'House of Hackney', category: 'G - M'},
        { key: 'IMO', label: 'IMO', category: 'G - M'},
        { key: 'IndiaMahdavi', label: 'India Mahdavi', category: 'G - M'},
        { key: 'IndieHomeCollective', label: 'Indie Home Collective', category: 'G - M'},
        { key: 'JamesSaid', label: 'James Said', category: 'G - M'},
        { key: 'Jardan', label: 'Jardan', category: 'G - M'},
        { key: 'JeanProuve', label: 'Jean Prouvé', category: 'G - M'},
        { key: 'JonathanAdler', label: 'Jonathan Adler', category: 'G - M'},
        { key: 'Kartell', label: 'Kartell', category: 'G - M'},
        { key: 'KingLiving', label: 'King Living', category: 'G - M'},
        { key: 'Knoll', label: 'Knoll', category: 'G - M'},
        { key: 'Kovacs', label: 'Kovacs', category: 'G - M'},
        { key: 'KPMBerlin', label: 'KPM Berlin', category: 'G - M'},
        { key: 'LaMaisonPierreFrey', label: 'La Maison Pierre Frey', category: 'G - M'},
        { key: 'LeeBroom', label: 'Lee Broom', category: 'G - M'},
        { key: 'Lema', label: 'Lema', category: 'G - M'},
        { key: 'LigneRoset', label: 'Ligne Roset', category: 'G - M'},
        { key: 'LivingDivani', label: 'Living Divani', category: 'G - M'},
        { key: 'LivingEdge', label: 'Living Edge', category: 'G - M'},
        { key: 'LoeweHome', label: 'Loewe Home', category: 'G - M'},
        { key: 'LouisPoulsen', label: 'Louis Poulsen', category: 'G - M'},
        { key: 'LouisVuittonHome', label: 'Louis Vuitton Home', category: 'G - M'},
        { key: 'Magis', label: 'Magis', category: 'G - M'},
        { key: 'MaidenHome', label: 'Maiden Home', category: 'G - M'},
        { key: 'MaisonCharles', label: 'Maison Charles', category: 'G - M'},
        { key: 'MaisonSarahLavoine', label: 'Maison Sarah Lavoine', category: 'G - M'},
        { key: 'MarieDaage', label: 'Marie Daâge', category: 'G - M'},
        { key: 'MarkTuckey', label: 'Mark Tuckey', category: 'G - M'},
        { key: 'MasonsIronstone', label: 'Mason\'s Ironstone', category: 'G - M'},
        { key: 'Matisse', label: 'Matisse', category: 'G - M'},
        { key: 'MCMHouse', label: 'MCM House', category: 'G - M'},
        { key: 'Menu', label: 'Menu', category: 'G - M'},
        { key: 'Meritalia', label: 'Meritalia', category: 'G - M'},
        { key: 'Minotti', label: 'Minotti', category: 'G - M'},
        { key: 'MisenDemeure', label: 'Mis en Demeure', category: 'G - M'},
        { key: 'Moissonnier', label: 'Moissonnier', category: 'G - M'},
        { key: 'Molteni&C', label: 'Molteni & C', category: 'G - M'},
        { key: 'Montana', label: 'Montana', category: 'G - M'},
        { key: 'Mulberry Home', label: 'Mulberry Home', category: 'G - M'},
        { key: 'Muuto', label: 'Muuto', category: 'G - M'},
        { key: 'NickScali', label: 'Nick Scali', category: 'N - T'},
        { key: 'OriginalBTC', label: 'Original BTC', category: 'N - T'},
        { key: 'PetiteFriture', label: 'Petite Friture', category: 'N - T'},
        { key: 'PierreFrey', label: 'Pierre Frey', category: 'N - T'},
        { key: 'Pillivuyt', label: 'Pillivuyt', category: 'N - T'},
        { key: 'Pinch', label: 'Pinch', category: 'N - T'},
        { key: 'Poliform', label: 'Poliform', category: 'N - T'},
        { key: 'PoltronaFrau', label: 'Poltrona Frau', category: 'N - T'},
        { key: 'Porro', label: 'Porro', category: 'N - T'},
        { key: 'Portmeirion', label: 'Portmeirion', category: 'N - T'},
        { key: 'RalphLaurenHome', label: 'Ralph Lauren Home', category: 'N - T'},
        { key: 'RaynaudLimoges', label: 'Raynaud Limoges', category: 'N - T'},
        { key: 'RichardGinori', label: 'Richard Ginori', category: 'N - T'},
        { key: 'Riva1920', label: 'Riva 1920', category: 'N - T'},
        { key: 'RocheBobois', label: 'Roche Bobois', category: 'N - T'},
        { key: 'Rose&Heather', label: 'Rose & Heather', category: 'N - T'},
        { key: 'RoyalCrownDerby', label: 'Royal Crown Derby', category: 'N - T'},
        { key: 'RoyalDoulton', label: 'Royal Doulton', category: 'N - T'},
        { key: 'SagexClare', label: 'Sage x Clare', category: 'N - T'},
        { key: 'Saint-Louis', label: 'Saint-Louis', category: 'N - T'},
        { key: 'SarahEllison', label: 'Sarah Ellison', category: 'N - T'},
        { key: 'Skagerak', label: 'Skagerak', category: 'N - T'},
        { key: 'SoaneBritain', label: 'Soane Britain', category: 'N - T'},
        { key: 'SohoHome', label: 'Soho Home', category: 'N - T'},
        { key: 'SorenLiv', label: 'Soren Liv', category: 'N - T'},
        { key: 'SpaceFurniture', label: 'Space Furniture', category: 'N - T'},
        { key: 'Spode', label: 'Spode', category: 'N - T'},
        { key: 'Stickley', label: 'Stickley', category: 'N - T'},
        { key: 'String', label: 'String', category: 'N - T'},
        { key: 'StudioItalia', label: 'Studio Italia', category: 'N - T'},
        { key: 'StudioMignone', label: 'Studio Mignone', category: 'N - T'},
        { key: 'Tait', label: 'Tait', category: 'N - T'},
        { key: 'Tessuti', label: 'Tessuti', category: 'N - T'},
        { key: 'TheConranShop', label: 'The Conran Shop', category: 'N - T'},
        { key: 'TheSofa&ChairCompany', label: 'The Sofa & Chair Company', category: 'N - T'},
        { key: 'TimWebberDesign', label: 'Tim Webber Design', category: 'N - T'},
        { key: 'TomDixon', label: 'Tom Dixon', category: 'N - T'},
        { key: 'TonySly', label: 'Tony Sly', category: 'N - T'},
        { key: 'TritHouse', label: 'Trit House', category: 'N - T'},
        { key: 'VersaceHome', label: 'Versace Home', category: 'U - Z'},
        { key: 'Villeroy&Boch', label: 'Villeroy & Boch', category: 'U - Z'},
        { key: 'VincentSheppard', label: 'Vincent Sheppard', category: 'U - Z'},
        { key: 'Vispring', label: 'Vispring', category: 'U - Z'},
        { key: 'Vitra', label: 'Vitra', category: 'U - Z'},
        { key: 'Wedgwood', label: 'Wedgwood', category: 'U - Z'},
        { key: 'WestElm', label: 'West Elm', category: 'U - Z'},
        { key: 'Zanotta', label: 'Zanotta', category: 'U - Z'},
        { key: 'Zuster', label: 'Zuster', category: 'U - Z'},

      ].sort((a, b) => a.label.localeCompare(b.label)),
    },
  },
  {
    id: 'productType',
    label: 'Product',
    type: 'SelectMultipleFilter',
    group: 'primary',
    queryParamNames: ['pub_productType'],
    config: {
      // Schema type options: 'enum', 'multi-enum'
      // Both types can work so that user selects multiple values when filtering search results.
      // With "enum" the functionality will be OR-semantics (Nike OR Adidas OR Salomon)
      // With "multi-enum" it's possible to use both AND and OR semantics with searchMode config.
      searchMode: 'has_any',

      // "key" is the option you see in Flex Console.
      // "label" is set here for the UI only.
      // Note: label is not added through the translation files
      // to make filter customizations a bit easier.
      options: [
        { key: 'ClassicSofas', label: 'Classic sofas', category: 'Couches' },
        { key: 'Daybeds', label: 'Daybeds', category: 'Couches' },
        { key: 'Chaises', label: 'Chaises', category: 'Couches' },
        { key: 'SofaBeds', label: 'Sofa beds', category: 'Couches' },
        { key: 'Modular', label: 'Modular', category: 'Couches' },
        { key: 'Ottomans', label: 'Ottomans', category: 'Couches' },
        { key: 'AccentChairs', label: 'Accent chairs', category: 'Chairs' },
        { key: 'HomeOfficeChairs', label: 'Home office chairs', category: 'Chairs' },
        { key: 'DiningChairs', label: 'Dining chairs', category: 'Chairs' },
        { key: 'Recliners', label: 'Recliners', category: 'Chairs' },
        { key: 'Stools', label: 'Stools', category: 'Chairs' },
        { key: 'Benches', label: 'Benches', category: 'Chairs' },
        { key: 'Armchairs', label: 'Armchairs', category: 'Chairs' },
        { key: 'Dressers', label: 'Dressers', category: 'Storage' },
        { key: 'Wardrobes', label: 'Wardrobes', category: 'Storage' },
        { key: 'Bookcases', label: 'Bookcases', category: 'Storage' },
        { key: 'Shelving', label: 'Shelving', category: 'Storage' },
        { key: 'Cabinets', label: 'Cabinets', category: 'Storage' },
        { key: 'Sideboards', label: 'Sideboards', category: 'Storage' },
        { key: 'Filing', label: 'Filing', category: 'Storage' },
        { key: 'Bins', label: 'Bins', category: 'Storage' },
        { key: 'EntertainmentUnits', label: 'Entertainment units', category: 'Storage' },
        { key: 'Trunks', label: 'Trunks', category: 'Storage' },
        { key: 'BedFrames ', label: 'Bed frames ', category: 'Beds' },
        { key: 'Headboards', label: 'Headboards', category: 'Beds' },
        { key: 'CoffeeTables', label: 'Coffee tables', category: 'Tables' },
        { key: 'EndTables', label: 'End tables', category: 'Tables' },
        { key: 'DiningTables', label: 'Dining tables', category: 'Tables' },
        { key: 'AccentTables', label: 'Accent tables', category: 'Tables' },
        { key: 'SideTables', label: 'Side tables', category: 'Tables' },
        { key: 'HomeOfficeDesks', label: 'Home office desks', category: 'Tables' },
        { key: 'UtilityTables', label: 'Utility tables', category: 'Tables' },
        { key: 'Lamps', label: 'Lamps', category: 'Interiors' },
        { key: 'WallArt', label: 'Wall art', category: 'Interiors' },
        { key: 'Rugs', label: 'Rugs', category: 'Interiors' },
        { key: 'Mirrors', label: 'Mirrors', category: 'Interiors' },
        { key: 'Frames', label: 'Frames', category: 'Interiors' },
        { key: 'IndoorPlants', label: 'Indoor plants', category: 'Interiors' },
        { key: 'Dividers', label: 'Dividers', category: 'Interiors' },
        { key: 'DecorativeAccents', label: 'Decorative accents', category: 'Interiors' },
        { key: 'Tables', label: 'Tables', category: 'Outdoor' },
        { key: 'SofasAndChairs', label: 'Sofas and chairs', category: 'Outdoor' },
        { key: 'Umbrellas', label: 'Umbrellas', category: 'Outdoor' },
        { key: 'ArtObject', label: 'Art & object', category: 'Art' },
        // { key: 'accessories', label: 'Accessories', category: 'Accessories' },
        { key: 'lighting', label: 'Lighting', category: 'Lighting' },

        { key: 'Furnishings', label: 'Furnishings', category: 'Interiors' },
        { key: 'Vases', label: 'Vases', category: 'Interiors' },
        { key: 'Sculptures', label: 'Sculptures', category: 'Interiors' },
        { key: 'Clocks', label: 'Clocks', category: 'Interiors' },
        { key: 'PlantersAndPots', label: 'Planters & pots', category: 'Interiors' },
        { key: 'TraysAndBowls', label: 'Trays & bowls', category: 'Interiors' },
        { key: 'CushionsAndThrows', label: 'Cushions & throws', category: 'Interiors' },
        { key: 'Serveware', label: 'Serveware', category: 'Interiors' },
        { key: 'CutleryAndUtensils', label: 'Cutlery & utensils', category: 'Interiors' },
        { key: 'DiningSets', label: 'Dining sets', category: 'Interiors' },
        { key: 'Collectibles', label: 'Collectibles', category: 'Interiors' },
        { key: 'Decorative', label: 'Decorative', category: 'Interiors' },
        { key: 'Books', label: 'Books', category: 'Interiors' },

        { key: 'TablewareAccessories', label: 'Tableware accessories', category: 'Tableware' },

        { key: 'StorageAndUtility', label: 'Storage & Utility', category: 'Storage' },
      ].sort((a, b) => a.label.localeCompare(b.label)),
    },
  },
  {
    id: 'room',
    label: 'Room',
    type: 'SelectMultipleFilter',
    group: 'primary',
    queryParamNames: ['pub_room'],
    config: {
      // Schema type options: 'enum', 'multi-enum'
      // Both types can work so that user selects multiple values when filtering search results.
      // With "enum" the functionality will be OR-semantics (Nike OR Adidas OR Salomon)
      // With "multi-enum" it's possible to use both AND and OR semantics with searchMode config.
      searchMode: 'has_any',

      // "key" is the option you see in Flex Console.
      // "label" is set here for the UI only.
      // Note: label is not added through the translation files
      // to make filter customizations a bit easier.
      options: [
        { key: 'bathroom', label: 'Bathroom' },
        { key: 'hallway', label: 'Hallway' },
        { key: 'bedroom', label: 'Bedroom' },

        { key: 'kitchen', label: 'Kitchen' },
        { key: 'dining', label: 'Dining' },

        { key: 'laundry', label: 'Laundry' },
        { key: 'living', label: 'Living' },
        { key: 'nursery', label: 'Nursery' },
        { key: 'office', label: 'Office' },
        { key: 'outdoor', label: 'Outdoor' },
      ].sort((a, b) => a.label.localeCompare(b.label)),
    },
  },
  {
    id: 'era',
    label: 'Era',
    type: 'SelectSingleFilter',
    group: 'primary',
    queryParamNames: ['pub_era'],
    config: {
      schemaType: 'enum',
      options: [
        { key: 'art_deco', label: 'Art deco' },
        { key: 'bauhaus', label: 'Bauhaus' },
        { key: 'chinese', label: 'Chinese' },
        { key: 'farmhouse', label: 'Farmhouse' },
        { key: 'french', label: 'French' },
        { key: 'industrial', label: 'Industrial' },
        { key: 'mid_century', label: 'Mid-century' },
        { key: 'modern', label: 'Modern' },
        { key: 'retro', label: 'Retro' },
        { key: 'scandinavian', label: 'Scandinavian' },
        { key: 'victorian', label: 'Victorian' },
        { key: 'other', label: 'Other' },
      ]
    },
  },
  // {
  //   id: 'furnitureType',
  //   label: 'Furniture type',
  //   type: 'SelectSingleFilter',
  //   group: 'primary',
  //   queryParamNames: ['pub_furnitureType'],
  //   config: {
  //     // Schema type is enum for SelectSingleFilter
  //     schemaType: 'enum',

  //     // "key" is the option you see in Flex Console.
  //     // "label" is set here for the UI only.
  //     // Note: label is not added through the translation files
  //     // to make filter customizations a bit easier.
  //     options: [
  //       { key: 'rugs', label: 'Rugs' },
  //       { key: 'coffee_tables', label: 'Coffee tables' },
  //       { key: 'sofas', label: 'Sofas' },
  //       { key: 'armchairs', label: 'Armchairs' },
  //       { key: 'dining_tables', label: 'Dining tables' },
  //       { key: 'side_tables', label: 'Side tables' },
  //       { key: 'storage', label: 'Storage' },
  //       { key: 'art_object', label: 'Art & object' },
  //       { key: 'frames_mirrors', label: 'Frames & mirrors' },
  //       { key: 'accessories', label: 'Accessories' },
  //       { key: 'lighting', label: 'Lighting' },
  //     ],
  //   },
  // },
  {
    id: 'condition',
    label: 'Condition',
    type: 'SelectSingleFilter',
    group: 'primary',
    queryParamNames: ['pub_condition'],
    config: {
      // Schema type is enum for SelectSingleFilter
      schemaType: 'enum',

      // "key" is the option you see in Flex Console.
      // "label" is set here for the UI only.
      // Note: label is not added through the translation files
      // to make filter customizations a bit easier.
      options: [
        { key: 'brand_new', label: 'Brand new' },
        { key: 'like_new', label: 'Like new' },
        { key: 'gently_used', label: 'Gently used' },
        { key: 'refurbished', label: 'Refurbished' },
        { key: 'worn', label: 'Worn' },
      ].sort((a, b) => a.label.localeCompare(b.label)),
    },
  },
  {
    id: 'price',
    label: 'Price',
    type: 'PriceFilter',
    group: 'primary',
    // Note: PriceFilter is fixed filter,
    // you can't change "queryParamNames: ['price'],"
    queryParamNames: ['price'],
    // Price filter configuration
    // Note: unlike most prices this is not handled in subunits
    config: {
      min: 0,
      max: 1000,
      step: 5,
    },
  },
  {
    id: 'keyword',
    label: 'Keyword',
    type: 'KeywordFilter',
    group: 'primary',
    // Note: KeywordFilter is fixed filter,
    // you can't change "queryParamNames: ['keywords'],"
    queryParamNames: ['keywords'],
    // NOTE: If you are ordering search results by distance
    // the keyword search can't be used at the same time.
    // You can turn on/off ordering by distance from config.js file.
    config: {},
  },

  // Here is an example of multi-enum search filter.
  //
  // {
  //   id: 'amenities',
  //   label: 'Amenities',
  //   type: 'SelectMultipleFilter',
  //   group: 'secondary',
  //   queryParamNames: ['pub_amenities'],
  //   config: {
  //     // Schema type options: 'enum', 'multi-enum'
  //     // Both types can work so that user selects multiple values when filtering search results.
  //     // With "enum" the functionality will be OR-semantics (Nike OR Adidas OR Salomon)
  //     // With "multi-enum" it's possible to use both AND and OR semantics with searchMode config.
  //     schemaType: 'multi-enum',

  //     // Optional modes: 'has_all', 'has_any'
  //     // Note: this is relevant only for schema type 'multi-enum'
  //     // https://www.sharetribe.com/api-reference/marketplace.html#extended-data-filtering
  //     searchMode: 'has_all',

  //     // "key" is the option you see in Flex Console.
  //     // "label" is set here for this web app's UI only.
  //     // Note: label is not added through the translation files
  //     // to make filter customizations a bit easier.
  //     options: [
  //       { key: 'towels', label: 'Towels' },
  //       { key: 'bathroom', label: 'Bathroom' },
  //       { key: 'swimming_pool', label: 'Swimming pool' },
  //       { key: 'barbeque', label: 'Barbeque' },
  //     ],
  //   },
  // },
];

export const sortConfig = {
  // Enable/disable the sorting control in the SearchPage
  active: true,

  // Note: queryParamName 'sort' is fixed,
  // you can't change it since Flex API expects it to be named as 'sort'
  queryParamName: 'sort',

  // Internal key for the relevance option, see notes below.
  relevanceKey: 'relevance',

  // Relevance key is used with keywords filter.
  // Keywords filter also sorts results according to relevance.
  relevanceFilter: 'keywords',

  // Keyword filter is sorting the results by relevance.
  // If keyword filter is active, one might want to disable other sorting options
  // by adding 'keyword' to this list.
  conflictingFilters: [],

  options: [
    { key: 'createdAt', label: 'Newest' },
    { key: '-createdAt', label: 'Oldest' },
    { key: '-price', label: 'Lowest price' },
    { key: 'price', label: 'Highest price' },

    // The relevance is only used for keyword search, but the
    // parameter isn't sent to the Marketplace API. The key is purely
    // for handling the internal state of the sorting dropdown.
    { key: 'relevance', label: 'Relevance', longLabel: 'Relevance' },
  ],
};

export const listing = {
  // These should be listing details from public data with schema type: enum
  // SectionDetailsMaybe component shows these on listing page.
  enumFieldDetails: ['size', 'brand', 'category'],
};
